// Shared enquiry: the rules the screens show, kept out of the .vue files so they can be unit tested.
// Nothing here talks to the server.
import { formatCurrency } from '@/utils/money'
import { existingSentence } from './customerQuotes'

// The job fields of MBP's Excel enquiry sheet (API keys from FIELD-MAP.md of the prototype), in the sheet's
// order, with the input each needs. The
// settings endpoint decides which of them are mandatory; a key the settings send that is not listed
// here is still shown, as a text box (so a field Session1 adds appears without a front-end change).
export const JOB_FIELD_TYPES = {
  dateReceived: 'date',
  orderDate: 'date',
  reqDeliveryDate: 'date',
  projectLength: 'text',
  doorsetScheduleRcvd: 'yesno',
  doorElevationsRcvd: 'yesno',
  floorPlansRcvd: 'yesno',
  specificationRcvd: 'yesno',
  qtyDoorsets: 'number',
  doorFacings: 'text',
  doorSizes: 'text',
  fireRatings: 'text',
  frameMaterial: 'text',
  wallWidths: 'text',
  visionPanel: 'text',
  ironmongeryRequested: 'yesno',
  otherInfo: 'textarea'
}

// The Excel sheet's own names for the fields (FIELD-MAP.md), shown when a settings row has no label, so a raw key
// never reaches the screen.
export const FIELD_LABELS = {
  project: 'Project Name',
  postcodeArea: 'Postcode area',
  dateReceived: 'Date received',
  orderDate: 'Date when order will be placed',
  reqDeliveryDate: 'Required delivery date',
  projectLength: 'Project Length',
  doorsetScheduleRcvd: 'Doorset Schedule Received',
  doorElevationsRcvd: 'Door Elevations Received',
  floorPlansRcvd: 'Floor Plans Received',
  specificationRcvd: 'Specification page Received',
  qtyDoorsets: 'Quantity of Doorsets',
  doorFacings: 'Door Facings',
  doorSizes: 'Door Sizes',
  fireRatings: 'Fire Ratings',
  frameMaterial: 'Frame Material',
  wallWidths: 'Wall widths',
  visionPanel: 'Vision Panel Design & Size',
  ironmongeryRequested: 'Quotation requested from MBP Ironmongery Solutions',
  otherInfo: 'Any other important information',
  customerId: 'Customer Name',
  contactId: 'Contact Name',
  phoneNo: 'Contact Number',
  email: 'Contact Email',
  projectTypeId: 'Status',
  chance: 'Chance of Order',
  branchMargin: 'Branch Margin',
  chaserUserId: 'Who will be chasing',
  productTypeId: 'Product type',
  branchId: 'Branch',
  salesExecId: 'Sales exec'
}

/** A settings row's label: the server's, else the sheet's name for the key, else a key split into words. */
export function fieldLabel(row) {
  const r = row || {}
  if (typeof r.label === 'string' && r.label.trim()) return r.label
  if (FIELD_LABELS[r.fieldKey]) return FIELD_LABELS[r.fieldKey]
  const words = String(r.fieldKey || '').replace(/Id$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// Bid-level keys the customer step asks for itself (FIELD-MAP.md); a mandatory setting on any of them is
// checked against the bid, not the job details.
export const BID_KEYS = ['customerId', 'contactId', 'phoneNo', 'email', 'productTypeId', 'branchId', 'projectTypeId',
  'salesExecId', 'chance', 'branchMargin', 'chaserUserId']

// Project type-ahead: the search runs this long after the last key press
export const TYPE_AHEAD_MS = 400

export const ATTACH_CATEGORIES = ['Doorset schedule', 'Door elevations', 'Floor plans', 'Specification', 'Other']

export const CHANCES = [
  { value: 'A', label: 'A (80%)' },
  { value: 'B', label: 'B (50-80%)' },
  { value: 'C', label: 'C (under 50%)' }
]

export const BASES = [
  { value: 'AVG', label: 'Average', word: 'Average' },
  { value: 'MAX', label: 'Highest', word: 'Highest' },
  { value: 'MIN', label: 'Lowest', word: 'Lowest' }
]

// A bid's form field for each bid-level setting key. The bid body uses ids (customerId...), the
// settings use the plain name; either spelling is accepted.
const BID_KEY_ALIASES = {
  customer: 'customerId',
  contact: 'contactId',
  productType: 'productTypeId',
  branch: 'branchId',
  quoteType: 'projectTypeId',
  projectType: 'projectTypeId',
  enquiryType: 'projectTypeId',
  salesExec: 'salesExecId',
  chaser: 'chaserUserId',
  whoWillBeChasing: 'chaserUserId',
  chanceOfOrder: 'chance'
}

// Project-level keys: answered by step 1, not by the job details.
const PROJECT_KEYS = { project: 'projectId', postcodeArea: 'postcodeArea' }

export function isBlank(v) {
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  return false
}

function bidValue(bid, key) {
  if (!bid) return undefined
  if (bid[key] !== undefined) return bid[key]
  const alias = BID_KEY_ALIASES[key] || key + 'Id'
  return bid[alias]
}

/**
 * What is still missing before the enquiry can be saved, as a list of plain sentences.
 *
 * settings: [{fieldKey, label, level: 'shared'|'bid', mandatory}]
 * project:  the picked project ({projectId, postcodeArea}) or null
 * job:      the job details, keyed by fieldKey
 * bids:     the customer bids still to be added (each checked on its own)
 *
 * A yes/no answer of "No" (false) counts as answered: only an untouched field is missing.
 */
export function missingMandatory(settings, { project, job, bids } = {}) {
  const missing = []
  const mandatory = (settings || []).filter(s => s.mandatory)
  mandatory.filter(s => s.level !== 'bid').forEach(s => {
    if (PROJECT_KEYS[s.fieldKey]) {
      // D3: an old (backfilled) project may have no postcode area; only new projects must have one
      if (s.fieldKey === 'postcodeArea' && project && project.legacy) return
      if (!project || isBlank(project[PROJECT_KEYS[s.fieldKey]])) missing.push(fieldLabel(s))
    } else if (!job || isBlank(job[s.fieldKey])) {
      missing.push(fieldLabel(s))
    }
  })
  const bidSettings = mandatory.filter(s => s.level === 'bid')
  ;(bids || []).forEach((bid, i) => {
    bidSettings.forEach(s => {
      if (isBlank(bidValue(bid, s.fieldKey))) {
        missing.push((bids.length > 1 ? 'Customer ' + (i + 1) + ': ' : '') + fieldLabel(s))
      }
    })
  })
  return missing
}

/**
 * How the customer check (D5) is shown next to a bid.
 *
 * check: the checkCustomer response {result, message, existing: [{oppId, sQuoteNo, branch, productType}]}
 * Returns {severity, text, canAdd, needsConfirm}:
 *   BLOCK: red, the existing quote number(s) and branch, add disabled
 *   WARN:  amber, add only after "Add anyway"
 *   OK:    nothing shown, add allowed
 *   no check yet (customer or product type not chosen): add disabled, nothing shown
 */
// Decision 18/10: BLOCK = same customer + product type + branch; WARN = another branch or another product type. The text
// names each existing customer quote with its branch (custName: the customer being checked); the list of those quotes,
// each with "Create New Version", is shown beside it.
export function checkDisplay(check, confirmed = false, custName = '') {
  if (!check || !check.result) return { severity: null, text: '', canAdd: false, needsConfirm: false }
  const rows = check.existing || []
  const named = rows.map(e => existingSentence(custName, e)).join(' ')
  if (check.result === 'BLOCK') {
    const text = (named || check.message || check.error || 'This customer already has this product type on this enquiry through this branch.') +
      ' Create a new version of that quotation instead.'
    return { severity: 'error', text, canAdd: false, needsConfirm: false }
  }
  if (check.result === 'WARN') {
    const text = (named || check.message || check.error || 'This customer already has a quotation on this enquiry.') +
      ' You can still add a new quotation, or create a new version of the existing one.'
    return { severity: 'warn', text, canAdd: confirmed, needsConfirm: !confirmed }
  }
  return { severity: null, text: '', canAdd: true, needsConfirm: false }
}

export function money(v) {
  if (v === null || v === undefined || v === '') return ''
  return formatCurrency(v)
}

/** "Average of 3 quotations: £95,000.00" for one product type's enquiry value. */
export function valueLabel(v) {
  if (!v) return ''
  const basis = BASES.find(b => b.value === v.basis)
  const word = basis ? basis.word : v.basis
  const n = v.n || 0
  if (!n) return 'No live quotations yet'
  return word + ' of ' + n + ' quotation' + (n === 1 ? '' : 's') + ': ' + money(v.value)
}

/** A project's line in the search results: "CROWBOROUGH CARE HOME · TN · Crowborough". */
export function projectLabel(p) {
  return [p.name, p.postcodeArea || (p.legacy ? 'no postcode area (old enquiry)' : ''), p.town].filter(Boolean).join(' · ')
}

export function phaseLabel(phases, phaseId) {
  if (!phaseId) return 'Main project'
  const ph = (phases || []).find(p => p.phaseId === phaseId)
  return ph ? 'Phase ' + ph.phaseNo + (ph.description ? ': ' + ph.description : '') : 'Phase ' + phaseId
}

export function yesNo(v) {
  if (v === true || v === 'Y' || v === 'YES') return 'Yes'
  if (v === false || v === 'N' || v === 'NO') return 'No'
  return ''
}

// The API sends and takes dates as dd/MM/yyyy (FIELD-MAP.md); the Calendar works in Date objects.
export function toDmy(d) {
  if (!d) return null
  if (typeof d === 'string') return d
  const p = n => (n < 10 ? '0' : '') + n
  return p(d.getDate()) + '/' + p(d.getMonth() + 1) + '/' + d.getFullYear()
}

export function fromDmy(s) {
  if (!s || typeof s !== 'string') return s || null
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null
}

/** The message a failed call carries: the server's own text when it sends one (the shared enquiry endpoints send {error}). */
export function errorText(e, fallback) {
  const d = e && e.response && e.response.data
  if (d && typeof d === 'string') return d
  if (d && d.error) return d.error
  if (d && d.message) return d.message
  return fallback || (e && e.message) || 'Something went wrong'
}

// D1: the edit form's label for each key of the server's jobLockedFields (EnquiryJobs.lockedJobFields)
export const JOB_LOCK_LABELS = {
  oppDescription: 'Description', address1: 'Address 1', address2: 'Address 2', cityTown: 'City/Town', postCode: 'Post Code'
}

/** The locked fields as the form labels them, for the note above the form; an unknown key shows as it is. */
export function jobLockLabels(keys) {
  return (keys || []).map(k => JOB_LOCK_LABELS[k] || k)
}

/** Today's status badge class for an enquiry status, as the Enquiries grid shows it (getStyle). */
export function statusBadge(name) {
  const n = String(name || '').toLowerCase()
  const kind = { active: 'badge-primary', lost: 'badge-danger', otp: 'badge-light', new: 'badge-info', won: 'badge-success' }[n]
  return 'badge ' + (kind || 'badge-secondary')
}

/** Design D's footer: "Project & job: 2 missing · Customer quote: 1 missing", or '' when nothing is missing. */
export function missingCounts(tabs) {
  return (tabs || []).filter(t => t.n > 0).map(t => t.tab + ': ' + t.n + ' missing').join(' · ')
}
